
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { KarmaActivity, KarmaAvailablePoints } from "../types/karma-types";
import { karmaClaimConfig } from "../utils/karmaClaimConfig";
import { useKarmaValidation } from "./useKarmaValidation";
import { useKarmaActivity } from "./useKarmaActivity";
import { useKarmaProfileUpdate } from "./useKarmaProfileUpdate";

export const useKarmaRewards = (
  karmaPoints: number,
  setKarmaPoints: (points: number) => void,
  activities: KarmaActivity[], // Keep this for type consistency, though setActivities is now in useKarmaActivity
  setActivities: React.Dispatch<React.SetStateAction<KarmaActivity[]>>,
  setAvailablePoints: React.Dispatch<React.SetStateAction<KarmaAvailablePoints>>,
  currentLevel: number,
  setCurrentLevel: (level: number) => void,
  fetchLeaderboard: () => Promise<void>
) => {
  const { user } = useAuth(); // Still needed for initial auth check
  const { toast } = useToast();

  const { canClaimPoints } = useKarmaValidation(setAvailablePoints);
  const { createKarmaActivityRecord } = useKarmaActivity(setActivities);
  const { updateUserProfileWithPoints } = useKarmaProfileUpdate(
    karmaPoints,
    setKarmaPoints,
    currentLevel,
    setCurrentLevel
  );

  const claimPoints = async (type: 'daily' | 'weekly' | 'referral') => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to claim points",
        variant: "destructive"
      });
      return;
    }

    try {
      // 1. Validate if points can be claimed (for daily/weekly)
      if (type === 'daily' || type === 'weekly') {
        const isEligible = await canClaimPoints(type);
        if (!isEligible) {
          return; // Validation hook already handled toast and setAvailablePoints
        }
      }
      
      // 2. Get claim configuration
      const claimDetails = karmaClaimConfig[type];
      if (!claimDetails) {
        console.error(`Invalid claim type: ${type}`);
        toast({ title: "Error", description: "Invalid claim type.", variant: "destructive" });
        return;
      }
      const { points: pointsToAdd } = claimDetails;

      // 3. Create Karma Activity Record
      await createKarmaActivityRecord(claimDetails);
      
      // 4. Update User Profile (Points & Level)
      await updateUserProfileWithPoints(pointsToAdd);
      
      // 5. Update UI for available points (if not referral)
      if (type !== 'referral') {
        setAvailablePoints(prev => ({
          ...prev,
          [type]: false
        }));
      }
      
      // 6. Success Toast
      toast({
        title: "Points Claimed!",
        description: `You earned ${pointsToAdd} karma points for ${claimDetails.title}!`,
      });
      
      // 7. Refresh leaderboard
      fetchLeaderboard();
      
    } catch (error: any) {
      console.error("Error claiming points in useKarmaRewards:", error.message, error);
      toast({
        title: "Error Claiming Points",
        description: "Could not claim points at this time. Please try again later.",
        variant: "destructive"
      });
    }
  };
  
  return { claimPoints };
};

